import { useEffect, useState } from "react";
import {
  useLive,
  post,
  formatPhone,
  type QueueItem,
  type Metrics,
  type Broker,
} from "../lib/api";
import { Card, Score, Pill, Button, Empty, Stat, StagePill, Avatar } from "../lib/ui";
import {
  Hourglass,
  UserPlus,
  Target,
  Timer,
  Scale,
  Clock,
  UserCheck,
  ThumbsDown,
  ThumbsUp,
  Headset,
  Send,
  PartyPopper,
} from "lucide-react";
import { get, ago } from "../lib/api";

/**
 * A tela que o corretor deixa aberta o dia inteiro.
 *
 * Se esta tela nao for boa, nada mais importa: e ela que decide se o
 * escalonamento vira atendimento ou vira lead esquecido. Por isso o topo e
 * quem esta esperando ha mais tempo com score mais alto, e a acao principal
 * ("assumir") esta a um clique, sem navegacao.
 */
export function Fila({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const { data: fila, reload } = useLive<QueueItem[]>("/queue", [
    "handoffs",
    "conversations",
    "messages",
    "leads",
  ]);
  const { data: m } = useLive<Metrics>("/metrics", ["handoffs", "leads", "messages"]);
  const { data: brokers } = useLive<Broker[]>("/brokers", []);
  // Vazio = eu, quem esta logado. So manda corretor quando alguem escolhe
  // outro na lista -- antes, sem nenhum cadastrado, "Assumir" nao fazia nada.
  const [broker, setBroker] = useState<string>("");

  async function assumir(item: QueueItem) {
    await post(`/conversations/${item.conversation_id}/claim`, {
      brokerId: broker || undefined,
    });
    reload();
    onOpenLead(item.lead_id);
  }

  async function avaliar(item: QueueItem, fair: boolean) {
    await post(`/handoffs/${item.handoff_id}/rate`, { fair });
    reload();
  }

  const esperando = fila?.filter((f) => f.control === "pending_human") ?? [];
  const emAtendimento = fila?.filter((f) => f.control === "human") ?? [];

  return (
    <div className="space-y-6">
      <Parados onOpenLead={onOpenLead} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat
          label="Esperando agora"
          value={m?.esperando_humano ?? "—"}
          tone={(m?.esperando_humano ?? 0) > 0 ? "warm" : undefined}
          hint="conversas paradas"
          icone={Hourglass}
        />
        <Stat
          label="Leads hoje"
          value={m?.leads_hoje ?? "—"}
          hint="entraram no funil"
          icone={UserPlus}
        />
        <Stat
          icone={Target}
          label="Qualificados 7d"
          value={m?.qualificados_7d ?? "—"}
          hint={`de ${m?.leads_7d ?? 0} na semana`}
        />
        <Stat
          icone={Timer}
          label="1ª resposta"
          value={
            m?.primeira_resposta_mediana_s != null
              ? `${Math.round(m.primeira_resposta_mediana_s)}s`
              : "—"
          }
          hint="mediana"
        />
        <Stat
          icone={Scale}
          label="Escalonamentos justos"
          value={m?.handoffs_justos_pct != null ? `${m.handoffs_justos_pct}%` : "—"}
          hint="o filtro está calibrado?"
        />
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            Esperando atendimento
            {esperando.length > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-warm-dim text-warm text-[11px] grid place-items-center">
                {esperando.length}
              </span>
            )}
          </h2>
          {brokers && brokers.length > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-ink-3">Atendendo como</span>
              <select
                value={broker}
                onChange={(e) => setBroker(e.target.value)}
                className="h-9 px-2.5 text-sm"
              >
                <option value="">Eu</option>
                {brokers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {esperando.length === 0 ? (
          <Empty icone={PartyPopper}>Ninguém esperando. A IA está dando conta.</Empty>
        ) : (
          esperando.map((item) => (
            <Card
              key={item.handoff_id}
              stripe={item.urgency === "alta" ? "danger" : "warm"}
              className="p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1 flex gap-3">
                  <Avatar nome={item.lead_name} />
                  <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => onOpenLead(item.lead_id)}
                      className="font-semibold text-ink hover:text-accent transition-colors"
                    >
                      {item.lead_name ?? "Sem nome"}
                    </button>
                    <span className="text-xs tabular-nums text-ink-3">
                      {formatPhone(item.lead_phone)}
                    </span>
                    <StagePill stage={item.stage} />
                    {item.urgency === "alta" && <Pill tone="danger">urgente</Pill>}
                  </div>

                  <p className="text-sm text-ink mt-1.5">{item.reason}</p>

                  {item.summary && (
                    <p className="text-sm text-ink-3 mt-1.5 leading-relaxed">
                      {item.summary}
                    </p>
                  )}

                  {item.last_message && (
                    <p className="text-sm text-ink-2 mt-3 px-3 py-2 rounded-lg bg-surface-2 border border-rule">
                      “{item.last_message}”
                    </p>
                  )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-3 shrink-0">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs tabular-nums px-2 py-1 rounded-md ${
                      item.waiting_minutes > 30
                        ? "text-danger bg-danger-dim"
                        : "text-ink-2 bg-surface-2"
                    }`}
                  >
                    <Clock size={12} />
                    esperando {item.waiting_minutes} min
                  </span>
                  <div className="w-44 space-y-1.5">
                    <Score value={item.fit_score} label="fit" />
                    <Score value={item.temp_score} label="temp" tone="warm" />
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      variant="ghost"
                      icone={ThumbsDown}
                      onClick={() => avaliar(item, false)}
                      title="A IA não precisava ter passado esta conversa"
                    >
                      Não valia
                    </Button>
                    <Button variant="primary" icone={UserCheck} onClick={() => assumir(item)}>
                      Assumir
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </section>

      {emAtendimento.length > 0 && (
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            Em atendimento humano
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-accent-dim text-accent text-[11px] grid place-items-center">
              {emAtendimento.length}
            </span>
          </h2>
          {emAtendimento.map((item) => (
            <Card key={item.handoff_id} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  onClick={() => onOpenLead(item.lead_id)}
                  className="flex items-center gap-3 text-sm text-ink hover:text-accent min-w-0"
                >
                  <Avatar nome={item.lead_name} tamanho="sm" />
                  <span className="font-medium">{item.lead_name ?? "Sem nome"}</span>
                  <span className="text-xs tabular-nums text-ink-3">
                    {formatPhone(item.lead_phone)}
                  </span>
                </button>
                <div className="flex items-center gap-2">
                  <Pill tone="accent">
                    <Headset size={11} />
                    {item.assigned_broker ?? "corretor"}
                  </Pill>
                  <Button
                    variant="ghost"
                    icone={ThumbsUp}
                    onClick={() => avaliar(item, true)}
                    title="Foi certo a IA passar esta conversa"
                  >
                    Valeu a pena
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </section>
      )}
    </div>
  );
}

interface Parado {
  lead_id: string;
  name: string | null;
  phone_e164: string;
  source: string;
  created_at: string;
  escreveu: boolean;
  motivo: string | null;
  outreach_status: string | null;
  payload: Record<string, unknown> | null;
}

/**
 * Leads que chegaram e nunca receberam mensagem nossa.
 *
 * Fica no topo da fila, e não numa aba própria, porque é um vazamento
 * silencioso: eles não aparecem na fila de escalonamento — ela só mostra
 * conversa que a IA entregou — e no funil parecem leads saudáveis na etapa
 * "novo". Numa aba separada, ninguém abriria.
 *
 * Some sozinho quando não há nenhum. Um bloco vazio permanente vira ruído e
 * treina a pessoa a ignorar aquela região da tela.
 */
function Parados({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const [lista, setLista] = useState<Parado[] | null>(null);
  const [indo, setIndo] = useState<string | null>(null);

  const recarregar = () =>
    get<Parado[]>("/leads/parados")
      .then(setLista)
      .catch(() => setLista([]));

  useEffect(() => {
    recarregar();
  }, []);

  if (!lista || lista.length === 0) return null;

  return (
    <Card className="p-4" stripe="danger">
      <div className="text-sm text-ink">
        {lista.length} lead{lista.length > 1 ? "s" : ""} sem nenhuma mensagem
        nossa
      </div>
      <p className="text-xs text-ink-3 mt-1 max-w-prose">
        Chegaram e não foram atendidos. Quem veio de formulário teve a abordagem
        recusada; quem escreveu está esperando resposta. Nenhum deles aparece na
        fila abaixo.
      </p>

      <div className="mt-3 space-y-2">
        {lista.map((p) => (
          <div key={p.lead_id} className="space-y-1">
          <div className="flex items-center gap-3 text-sm">
            <button
              className="text-ink hover:underline text-left"
              onClick={() => onOpenLead(p.lead_id)}
            >
              {p.name ?? formatPhone(p.phone_e164)}
            </button>
            <span className="text-ink-3 text-xs">há {ago(p.created_at)}</span>
            {p.escreveu ? (
              <Pill tone="danger">escreveu e não foi respondido</Pill>
            ) : (
              <span className="text-xs text-ink-3">
                {p.motivo ?? `veio de ${p.source}`}
              </span>
            )}
            <Button
              className="ml-auto"
              icone={Send}
              disabled={indo === p.lead_id}
              onClick={async () => {
                setIndo(p.lead_id);
                try {
                  await post(`/leads/${p.lead_id}/abordar`);
                } catch {
                  /* o motivo aparece na próxima carga da lista */
                }
                setIndo(null);
                recarregar();
              }}
            >
              {indo === p.lead_id ? "enviando…" : "Abordar agora"}
            </Button>
          </div>
          {/* O que a pessoa escreveu no site. É o único contexto que existe
              antes de alguém decidir resgatar esse contato. */}
          {typeof p.payload?.mensagem === "string" &&
            p.payload.mensagem.trim() !== "" && (
              <p className="text-xs text-ink-3 pl-1 italic">
                “{String(p.payload.mensagem)}”
              </p>
            )}
          </div>
        ))}
      </div>

      <p className="text-xs text-ink-3 mt-3">
        Fora do horário comercial a abordagem sai na próxima abertura, não na
        hora. Abordar relê o formulário com o entendimento atual — leads
        antigos ganham os dados que ainda não eram extraídos na época.
      </p>
    </Card>
  );
}
